import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { useStudent } from "../context/StudentContext";
import useDropdownMenu from "../hooks/useDropdownMenu";
import { API_URL } from "../config";
import { clearSessionKeys } from "../utils/sessionKeys";
import { getHomeRouteByRole } from "../utils/roles";
import HeaderBrandLogo from "./HeaderBrandLogo";
import ConstrainedSelect from "./ConstrainedSelect";
import { getMessage } from "../data/locales";
import "./SidebarUser.css";

const ALUMNO_HOME_ROUTE = getHomeRouteByRole("alumno");

const StudentHeader = ({ showHamburger = false, sidebarOpen = false, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { menuAbierto, toggleMenu, closeMenu, menuRef, handleMouseEnter, handleMouseLeave } = useDropdownMenu();
  const { courses, activeCourse, setActiveCourse } = useStudent();
  const [logoutError, setLogoutError] = useState(null);
  const [logoutInProgress, setLogoutInProgress] = useState(false);

  const handleLogout = async () => {
    if (logoutInProgress) return;
    setLogoutInProgress(true);
    setLogoutError(null);
    try {
      const response = await fetch(`${API_URL}/logout`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok || response.status === 401 || response.status === 403) {
        clearSessionKeys();
        navigate("/login", { replace: true });
        return;
      }
      console.error("Logout failed with status:", response.status);
      setLogoutError(getMessage("student.header.logoutError"));
    } catch (e) {
      console.error("Error de red al cerrar sesión:", e);
      setLogoutError(getMessage("student.header.logoutConnectionError"));
    } finally {
      setLogoutInProgress(false);
    }
  };

  const handleCourseChange = (courseId) => {
    const selected = courses.find((c) => c._id === courseId);
    if (selected) {
      setActiveCourse(selected);
      window.dispatchEvent(new CustomEvent('courseChanged', {
        detail: { courseId: selected._id }
      }));
      navigate("/usuario/campus");
    }
  };

  const handleHamburgerClick = () => {
    if (menuAbierto) {
      closeMenu();
    }
    onToggleSidebar?.();
  };

  return (
    <div className="side-header side-header--student">
      {/* Left: Hamburger & Logo - Justify Start */}
      <div className="side-header-brand">
        {showHamburger && (
          <button
            type="button"
            className="side-hamburger"
            onClick={handleHamburgerClick}
            aria-expanded={sidebarOpen}
            aria-controls="student-course-sidebar"
            aria-label={
              sidebarOpen
                ? getMessage("student.header.closeMenu")
                : getMessage("student.header.openMenu")
            }
          >
            ☰
          </button>
        )}
        <HeaderBrandLogo linkTo={ALUMNO_HOME_ROUTE} />
      </div>

      {/* Center: Course Selector - Justify Center */}
      <div className="side-middle-container">
        {courses.length > 1 ? (
          <ConstrainedSelect
            className="course-selector"
            value={activeCourse?._id || ""}
            onChange={handleCourseChange}
            ariaLabel={getMessage("student.header.selectCourse")}
            options={courses.map((course) => ({
              value: course._id,
              label: course.titulo || course.nombre || getMessage("student.header.unnamedCourse"),
            }))}
          />
        ) : (
          <h2 className="side-header-course-title">
            {courses.length === 0 && !activeCourse
              ? getMessage("student.header.noCourses")
              : (activeCourse?.titulo || activeCourse?.nombre || getMessage("student.header.unnamedCourse"))}
          </h2>
        )}
      </div>

      {/* Right: User Menu - Justify End */}
      <div
        className="side-user-menu"
        ref={menuRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          type="button"
          className="user-profile-trigger"
          onClick={toggleMenu}
          aria-haspopup="menu"
          aria-expanded={menuAbierto}
          aria-label={getMessage("student.header.profileMenu")}
        >
           <img
            src="/logos/LOGO_PERFIL.png"
            alt=""
            className="side-user-icon ui-integrated-image"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        </button>
        
        {menuAbierto && (
          <div className="side-dropdown" role="menu">
            <Link to="/usuario/perfil" className="side-dropdown-item" role="menuitem">
              {getMessage("student.header.profile")}
            </Link>
            {logoutError && (
              <div style={{ color: "#ff6b6b", fontSize: "12px", padding: "5px 12px" }}>
                {logoutError}
              </div>
            )}
            <button
              type="button"
              className="side-dropdown-item"
              role="menuitem"
              onClick={handleLogout}
              disabled={logoutInProgress}
              aria-busy={logoutInProgress}
            >
              {logoutInProgress
                ? getMessage("student.header.logoutInProgress")
                : getMessage("student.header.logout")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

StudentHeader.propTypes = {
  showHamburger: PropTypes.bool,
  sidebarOpen: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
};

export default StudentHeader;
